import type {
  AggregateMinAvgMax,
  AggregateResult,
  AggregateRow,
  ParameterKey,
  Torch
} from '../weldParameterAggregator'
import { bugTypesFromAlxPassName, isRootPassName } from './iwmRootMappingPreview'

/** Flip Root (CW) ↔ Root (CCW) in a pass label when present. */
export function flipRootDirectionalPassName(passName: string): string {
  if (/\(\s*CCW\s*\)/i.test(passName)) {
    return passName.replace(/\(\s*CCW\s*\)/i, '(CW)')
  }
  if (/\(\s*CW\s*\)/i.test(passName)) {
    return passName.replace(/\(\s*CW\s*\)/i, '(CCW)')
  }
  return passName
}

function rootDirectionFromPass(pass: string): 'CW' | 'CCW' | null {
  if (!isRootPassName(pass)) return null
  const fromLabel = bugTypesFromAlxPassName(pass)
  return fromLabel[0] ?? null
}

/**
 * When swap is on, exchange values between Root (CW) and Root (CCW) aggregate
 * rows so Welding Parameters matches report-time Root CW↔CCW mapping.
 * Stored aggregate is not mutated; returns a shallow-copied result.
 */
export function applyIwmRootSwapToAggregate(
  aggregate: AggregateResult,
  swapEnabled: boolean
): AggregateResult {
  if (!swapEnabled) return aggregate

  const cwPass = aggregate.rows.find((r) => rootDirectionFromPass(r.pass) === 'CW')
  const ccwPass = aggregate.rows.find((r) => rootDirectionFromPass(r.pass) === 'CCW')
  if (!cwPass || !ccwPass) return aggregate

  const byPass = new Map(aggregate.rows.map((r) => [r.pass, r]))
  const swapPair = (a: AggregateRow, b: AggregateRow): AggregateRow => ({
    ...a,
    values: b.values,
    sampleCount: b.sampleCount,
    torches: b.torches,
    wpsPass: a.wpsPass
  })

  byPass.set(cwPass.pass, swapPair(cwPass, ccwPass))
  byPass.set(ccwPass.pass, swapPair(ccwPass, cwPass))

  return {
    rows: aggregate.rows.map((r) => byPass.get(r.pass) ?? r)
  }
}

export interface ManualRowLike {
  pass: string
  torch: Torch
  values: Partial<Record<ParameterKey, Partial<AggregateMinAvgMax>>>
}

/**
 * Same value exchange for manual WP rows labeled Root (CW) / Root (CCW).
 */
export function applyIwmRootSwapToManualRows<T extends ManualRowLike>(
  rows: T[],
  swapEnabled: boolean
): T[] {
  if (!swapEnabled) return rows
  const cw = rows.find((r) => rootDirectionFromPass(r.pass) === 'CW')
  const ccw = rows.find((r) => rootDirectionFromPass(r.pass) === 'CCW')
  if (!cw || !ccw) return rows

  return rows.map((r) => {
    if (r.pass === cw.pass) {
      return { ...r, torch: ccw.torch, values: { ...ccw.values } }
    }
    if (r.pass === ccw.pass) {
      return { ...r, torch: cw.torch, values: { ...cw.values } }
    }
    return r
  })
}
